/* Custom next/image loader (next.config.ts `images.loaderFile`).
 *
 * Every catalog image lives in the R2 public bucket as a 1024×1024 RGBA
 * PNG of 1–2.4 MB. Served raw, a sticker page weighs 17 MB and its LCP on
 * a throttled phone is 25 s (Lighthouse, 2026-09-06). Vercel's Image
 * Optimization product is off by a documented cost decision (see the
 * 2026-05-18 note in next.config.ts), so R2 sources are routed to our
 * own resize route instead: /api/img — sharp on the function, cached by
 * the CDN, never /_next/image, never that bill.
 *
 * Anything not in the R2 bucket is returned untouched, which is exactly
 * what `unoptimized: true` did for it. Widths snap to a fixed ladder so
 * the CDN holds at most three variants per source. */

export const R2_PUBLIC_HOST = "pub-284b554e3416444f825505d3afe4aa82.r2.dev";
const R2_PUBLIC_BASE = `https://${R2_PUBLIC_HOST}/`;

/* Sources are 1024 px; nothing is ever upscaled. */
export const IMG_WIDTHS = [320, 640, 1024] as const;
export type ImgWidth = (typeof IMG_WIDTHS)[number];

export function snapWidth(width: number): ImgWidth {
  for (const w of IMG_WIDTHS) if (width <= w) return w;
  return IMG_WIDTHS[IMG_WIDTHS.length - 1] as ImgWidth;
}

export function isResizableR2(src: string): boolean {
  return src.startsWith(R2_PUBLIC_BASE) && /\.(png|jpe?g|webp)$/i.test(src);
}

/* Signed URLs into the PRIVATE bucket (Notion covers, business-kit covers:
 * 2.3 MB PNGs the backend hands out as
 * https://<account>.r2.cloudflarestorage.com/apex-assets/<key>?X-Amz-…).
 * The key is stable even though the signature is not, so the resized URL
 * is cacheable. /api/img reads the object with the storefront's own R2
 * credentials. */
const R2_SIGNED_RE = /^https:\/\/[a-f0-9]{32}\.r2\.cloudflarestorage\.com\/apex-assets\/([^?]+)/i;
/* The backend proxy does not resize keys under apex/ (only shop/): a
 * request for /api/shop/image/apex/notion-covers/x.png?width=… still
 * streams the 2.3 MB original. Those keys live in the private bucket too,
 * so they take the same /api/img?k= road. Keys arrive either bare
 * ("apex/notion-covers/x.png") or inside a proxy URL, sometimes with the
 * slashes percent-encoded. */
const PROXY_APEX_RE = /\/api\/shop\/image\/(apex(?:%2F|\/)[^?]+)/i;
export function privateR2Key(src: string): string | null {
  const m = src.match(R2_SIGNED_RE) ?? src.match(PROXY_APEX_RE);
  let key: string | null = null;
  if (m) key = decodeURIComponent(m[1]!);
  else if (/^apex\/[^?]+$/i.test(src)) key = src;
  if (!key) return null;
  return /\.(png|jpe?g|webp)$/i.test(key) ? key : null;
}

export default function r2ImageLoader({ src, width }: { src: string; width: number; quality?: number }): string {
  if (isResizableR2(src)) {
    const key = src.slice(R2_PUBLIC_BASE.length);
    return `/api/img?u=${encodeURIComponent(key)}&w=${snapWidth(width)}`;
  }
  const pk = privateR2Key(src);
  if (pk) return `/api/img?k=${encodeURIComponent(pk)}&w=${snapWidth(width)}`;
  return src;
}
